import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { scrollToId } from "./Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1569629743817-70d8db6c323b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwYWlycGxhbmUlMjBmbHlpbmd8ZW58MHx8fHwxNzg2NDEyMDIzfDA&ixlib=rb-4.1.0&q=85&w=2000";

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Commercial aircraft in flight" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/60 to-[#020617]/30" />
      </motion.div>

      <div className="relative z-10 flex-1 flex flex-col justify-end max-w-7xl mx-auto w-full px-6 pt-40 pb-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-overline"
          className="font-mono-tech text-xs md:text-sm tracking-[0.25em] uppercase text-[#60A5FA] mb-6"
        >
          {t.hero.overline}
        </motion.p>

        <h1 data-testid="hero-title" className="font-display font-black tracking-tighter leading-[0.92] text-[15vw] md:text-[9.5vw] lg:text-[8vw]">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className="block"
              >
                {i === 1 ? <span className="text-[#2563EB]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            data-testid="hero-subtitle"
            className="max-w-xl text-slate-300 text-base md:text-lg leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => scrollToId("services")}
              className="px-7 py-3.5 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium transition-all duration-300 hover:scale-[1.04]"
            >
              {t.hero.ctaPrimary}
            </button>
            <button
              data-testid="hero-contact-btn"
              onClick={() => scrollToId("contact")}
              className="px-7 py-3.5 rounded-full border border-white/25 hover:border-[#2563EB] hover:text-[#60A5FA] text-white font-medium transition-all duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          data-testid="hero-stats"
          className="mt-14 grid grid-cols-3 border-t border-white/15"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} className={`py-6 ${i > 0 ? "border-l border-white/15 pl-6" : ""}`}>
              <div className="font-display font-bold text-3xl md:text-5xl text-white">{s.value}</div>
              <div className="font-mono-tech text-[10px] md:text-xs uppercase tracking-widest text-slate-400 mt-2">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("about")}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
      >
        <span className="font-mono-tech text-xs uppercase tracking-widest">{t.hero.scroll}</span>
        <ChevronDown size={16} />
      </motion.button>
    </section>
  );
};
